import React, { useState } from 'react';
import { auth } from '../services/firebaseConfig';
import { 
  createUserWithEmailAndPassword, 
  GoogleAuthProvider,
  signInWithPopup
} from "firebase/auth";
import { useNavigate, Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { logGoogleSignInAttempt, logGoogleSignInSuccess, logGoogleSignInError } from '../services/analyticsLogger';
import { trackCompleteRegistration } from '../services/fbPixel';
import '../styles/RegisterPage.css';
import { getAuth } from 'firebase/auth';
import accountService from '../services/accountService';

const RegisterPage = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [dob, setDob] = useState('');
  const [genre, setGenre] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const navigate = useNavigate();
  const googleProvider = new GoogleAuthProvider();

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Regular expression to validate phone number format
    const phoneRegex = /^09\d{9}$/;

    if (!phoneRegex.test(phoneNumber)) {
      setError('Phone number must be 11 digits and start with 09');
      setIsLoading(false);
      return;
    }

    try {
      const auth = getAuth();
      // Register with Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Create initial user profile in Firestore
      await accountService.createUserProfile(user.uid, {
        name: `${firstName} ${lastName}`,
        email: user.email,
        phone: phoneNumber,
        city: city,
        province: province,
        birthdate: dob,
        favoriteGenre: genre,
        createdAt: new Date(),
        updatedAt: new Date(),
        avatar: null,
        plan: 'free'  // default plan
      });

      // Redirect to home page after successful registration
      trackCompleteRegistration({ method: 'email' });
      navigate('/');
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    setError('');
    
    try {
      console.log('🔐 Starting Google sign-up...');
      logGoogleSignInAttempt('register');
      
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      console.log('✅ Google sign-up successful:', {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName
      });
      
      logGoogleSignInSuccess(user.uid, user.email, 'register');
      
      let profileExists = false;
      
      try {
        await accountService.getUserProfile(user.uid);
        profileExists = true;
        console.log('ℹ️ User profile already exists');
      } catch (profileError) {
        if (profileError.response?.status === 404) {
          profileExists = false;
          console.log('ℹ️ User profile does not exist, creating new profile...');
        } else {
          throw profileError;
        }
      }
      
      if (!profileExists) {
        trackCompleteRegistration({ method: 'google' });
        await accountService.createUserProfile(user.uid, {
          name: user.displayName || '',
          email: user.email,
          phone: '',
          city: '',
          province: '',
          birthdate: '',
          favoriteGenre: '',
          createdAt: new Date(),
          updatedAt: new Date(),
          avatar: user.photoURL || null,
          plan: 'free'
        });
        console.log('✅ User profile created successfully');
      }
      
      navigate('/');
      
    } catch (error) {
      console.error('❌ Google sign-up error:', {
        code: error.code,
        message: error.message,
        fullError: error
      });
      
      logGoogleSignInError(error.code, error.message, 'register');
      
      // Handle specific error cases
      if (error.code === 'auth/popup-closed-by-user') {
        console.log('ℹ️ User closed the popup');
        // Don't show error, but ensure loading state is cleared
      } else if (error.code === 'auth/popup-blocked') {
        setError('Popup was blocked by your browser. Please allow popups for this site.');
      } else if (error.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorized for Google Sign-In. Please contact support.');
        console.error('⚠️ CRITICAL: Domain not authorized in Firebase Console');
      } else if (error.code === 'auth/operation-not-allowed') {
        setError('Google Sign-In is not enabled. Please contact support.');
        console.error('⚠️ CRITICAL: Google provider not enabled in Firebase Console');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection and try again.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        console.log('ℹ️ Multiple popup requests - user likely clicked button multiple times');
      } else {
        setError(`Failed to sign up with Google: ${error.message}`);
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <>
      <div className="register-container">
        <div className="register-content">
          <div className="register-header">
            <h1>Create Account</h1>
            <p>Join our karaoke community today!</p>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="social-login">
            <div className="social-buttons">
              <button 
                onClick={handleGoogleLogin}
                className={`google-login-button ${isGoogleLoading ? 'loading' : ''}`}
                disabled={isLoading || isGoogleLoading}
              >
                {isGoogleLoading ? (
                  <span className="loading-spinner"></span>
                ) : (
                  <>
                    <i className="fab fa-google"></i>
                    Continue with Google
                  </>
                )}
              </button>
            </div>
            <div className="social-login-divider">
              <span>or</span>
            </div>
          </div>

          <form onSubmit={handleRegister} className="register-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <div className="input-wrapper">
                  <i className="fas fa-user"></i>
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter your first name"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <div className="input-wrapper">
                  <i className="fas fa-user"></i>
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter your last name"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <div className="input-wrapper">
                <i className="fas fa-envelope"></i>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <i className="fas fa-lock"></i>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">City</label>
                <div className="input-wrapper">
                  <i className="fas fa-city"></i>
                  <input
                    id="city"
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Enter your city"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="province">Province</label>
                <div className="input-wrapper">
                  <i className="fas fa-map-marker-alt"></i>
                  <input
                    id="province"
                    type="text"
                    value={province}
                    onChange={(e) => setProvince(e.target.value)}
                    placeholder="Enter your province"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dob">Date of Birth</label>
                <div className="input-wrapper">
                  <i className="fas fa-calendar"></i>
                  <input
                    id="dob"
                    type="date"
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="genre">Favorite Genre</label>
                <div className="input-wrapper">
                  <i className="fas fa-music"></i>
                  <input
                    id="genre"
                    type="text"
                    value={genre}
                    onChange={(e) => setGenre(e.target.value)}
                    placeholder="Enter favorite genre"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number</label>
              <div className="input-wrapper">
                <i className="fas fa-phone"></i>
                <input
                  id="phoneNumber"
                  type="text"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="Enter your phone number (09xxxxxxxxx)"
                  required
                />
              </div>
            </div>

            <button 
              type="submit" 
              className={`register-button ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="loading-spinner"></span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="register-footer">
            <p>Already have an account? <Link to="/login">Sign in</Link></p>
          </div>
        </div>

        <div className="register-background">
          <div className="background-overlay"></div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default RegisterPage;
